import type { EventTable, EventTableKind } from "@hobbyshopify/backend-client";
import type { FormEvent } from "react";
import { useObserver, useSubtree } from "subtree.js/react";
import { formatAmount } from "../../../../core/primitives/money";
import {
  mayTakeBack,
  payingCount,
  type TableKindDraft,
  type TableKindErrors,
  type TableKindField,
  tableLabels,
  takenCount,
  takenLine,
  takenTables,
} from "../../../../core/primitives/table-kind";
import { TableKindsSectionActions, TableKindsSectionState } from "./table-kinds-section-model";

const labelCls = "mb-1.5 block text-xs font-medium text-white";

function KindFields({
  draft,
  errors,
  onChange,
  idPrefix,
}: {
  draft: TableKindDraft;
  errors: TableKindErrors;
  onChange: (field: TableKindField, value: string) => void;
  idPrefix: string;
}) {
  return (
    <>
      <div>
        <label htmlFor={`${idPrefix}-name`} className={labelCls}>
          Kind of table
        </label>
        <input
          id={`${idPrefix}-name`}
          value={draft.name}
          onChange={(e) => onChange("name", e.target.value)}
          className="field"
          placeholder="e.g. 8-foot table + chairs"
          maxLength={100}
        />
        {errors.name && <p className="field-error">{errors.name}</p>}
      </div>
      <div className="grid gap-2.5 sm:grid-cols-2">
        <div>
          <label htmlFor={`${idPrefix}-price`} className={labelCls}>
            Price
          </label>
          <input
            id={`${idPrefix}-price`}
            value={draft.price}
            onChange={(e) => onChange("price", e.target.value)}
            className="field"
            inputMode="decimal"
            placeholder="80.00"
          />
          {errors.price && <p className="field-error">{errors.price}</p>}
        </div>
        <div>
          <label htmlFor={`${idPrefix}-quantity`} className={labelCls}>
            How many
          </label>
          <input
            id={`${idPrefix}-quantity`}
            value={draft.quantity}
            onChange={(e) => onChange("quantity", e.target.value)}
            className="field"
            inputMode="numeric"
          />
          {errors.quantity && <p className="field-error">{errors.quantity}</p>}
        </div>
      </div>
      <div>
        <label htmlFor={`${idPrefix}-prefix`} className={labelCls}>
          Letters <span className="font-normal text-faint">Optional</span>
        </label>
        <input
          id={`${idPrefix}-prefix`}
          value={draft.prefix}
          onChange={(e) => onChange("prefix", e.target.value)}
          className="field"
          placeholder="Next free letter: A, B, C…"
          maxLength={3}
        />
        <p className="mt-1 text-xs text-faint">Tables of this kind read A-1, A-2, … with these letters.</p>
        {errors.prefix && <p className="field-error">{errors.prefix}</p>}
      </div>
      <div>
        <label htmlFor={`${idPrefix}-description`} className={labelCls}>
          What sets it apart <span className="font-normal text-faint">Optional</span>
        </label>
        <input
          id={`${idPrefix}-description`}
          value={draft.description}
          onChange={(e) => onChange("description", e.target.value)}
          className="field"
          placeholder="e.g. By the entrance, power included"
          maxLength={1000}
        />
      </div>
      <div>
        <label htmlFor={`${idPrefix}-private-description`} className={labelCls}>
          Note for the shop that buys one <span className="font-normal text-faint">Optional</span>
        </label>
        <textarea
          id={`${idPrefix}-private-description`}
          value={draft.privateDescription}
          onChange={(e) => onChange("privateDescription", e.target.value)}
          className="field min-h-16"
          placeholder="e.g. Thanks for coming! Your free Pro account: promo code HobbyShow"
          maxLength={1000}
        />
        <p className="mt-1 text-xs text-faint">
          Only that shop sees it, once it has paid: with its table, and in the email it gets.
        </p>
      </div>
    </>
  );
}

function TakenTable({
  table,
  cancelled,
  confirming,
  refundingId,
}: {
  table: EventTable;
  cancelled: boolean;
  confirming: boolean;
  refundingId: string | null;
}) {
  const actions = useSubtree(TableKindsSectionActions);
  const refunding = refundingId === table.id;
  return (
    <li className="flex flex-wrap items-center gap-2 text-xs">
      <span className="min-w-40 flex-1 text-muted">
        Table {table.label} · {takenLine(table)}
      </span>
      {!cancelled &&
        mayTakeBack(table) &&
        (confirming ? (
          <>
            <span>Refund the shop and put the table back on sale?</span>
            <button
              type="button"
              className="btn-destructive btn-sm"
              disabled={refunding}
              onClick={() => void actions.takeBack(table.id)}
            >
              {refunding ? "Refunding…" : "Yes, refund"}
            </button>
            <button type="button" className="btn-ghost btn-sm" disabled={refunding} onClick={actions.keepTable}>
              Keep
            </button>
          </>
        ) : (
          <button
            type="button"
            className="btn-ghost btn-sm"
            disabled={refundingId !== null}
            onClick={() => void actions.takeBack(table.id)}
          >
            Take back
          </button>
        ))}
    </li>
  );
}

function KindRow({
  kind,
  removing,
  cancelled,
  confirmRefundId,
  refundingId,
}: {
  kind: EventTableKind;
  removing: boolean;
  cancelled: boolean;
  confirmRefundId: string | null;
  refundingId: string | null;
}) {
  const actions = useSubtree(TableKindsSectionActions);
  const taken = takenCount(kind);
  const paying = payingCount(kind);
  const tables = takenTables(kind);
  return (
    <li className="flex flex-wrap items-center gap-3 rounded-row border border-line px-3.5 py-3">
      <div className="min-w-48 flex-1 space-y-0.5">
        <p className="text-[13px] font-medium text-white">
          {kind.name} · {formatAmount(kind.priceCents, kind.currency)}
        </p>
        {kind.description && <p className="text-xs text-muted">{kind.description}</p>}
        {kind.privateDescription && (
          <p className="whitespace-pre-line text-xs text-muted">For its buyer only: {kind.privateDescription}</p>
        )}
        <p className="text-xs text-muted">
          {kind.tables.length} {kind.tables.length === 1 ? "table" : "tables"}, {tableLabels(kind)} ·{" "}
          {taken === 0 ? "none taken yet" : `${taken} taken`}
          {paying > 0 && ` · ${paying} awaiting payment`}
        </p>
        {tables.length > 0 && (
          <ul className="space-y-1 pt-1">
            {tables.map((table) => (
              <TakenTable
                key={table.id}
                table={table}
                cancelled={cancelled}
                confirming={confirmRefundId === table.id}
                refundingId={refundingId}
              />
            ))}
          </ul>
        )}
      </div>
      {!cancelled && (
        <div className="flex shrink-0 gap-2">
          <button type="button" className="btn-ghost" onClick={() => actions.startEdit(kind.id)}>
            Edit
          </button>
          {taken + paying === 0 && (
            <button
              type="button"
              className="btn-ghost"
              disabled={removing}
              onClick={() => void actions.remove(kind.id)}
            >
              {removing ? "Removing…" : "Remove"}
            </button>
          )}
        </div>
      )}
    </li>
  );
}

export function TableKindsSection() {
  const state = useSubtree(TableKindsSectionState);
  const actions = useSubtree(TableKindsSectionActions);
  const observer = useObserver();
  const status = observer.watch(state.status);
  const kinds = observer.watch(state.kinds);
  const draft = observer.watch(state.draft);
  const errors = observer.watch(state.errors);
  const addForm = observer.watch(state.addForm);
  const editingId = observer.watch(state.editingId);
  const editDraft = observer.watch(state.editDraft);
  const editErrors = observer.watch(state.editErrors);
  const editForm = observer.watch(state.editForm);
  const removingId = observer.watch(state.removingId);
  const removeError = observer.watch(state.removeError);
  const cancelled = observer.watch(state.cancelled);
  const confirmRefundId = observer.watch(state.confirmRefundId);
  const refundingId = observer.watch(state.refundingId);
  const refundError = observer.watch(state.refundError);

  const add = (e: FormEvent) => {
    e.preventDefault();
    void actions.add();
  };
  const save = (e: FormEvent) => {
    e.preventDefault();
    void actions.saveEdit();
  };

  return (
    <section className="panel space-y-4 p-4">
      <div>
        <h2 className="text-base font-semibold">Tables</h2>
        <p className="text-sm opacity-60">
          What other shops take at this show. Each kind has its letters, and its tables read A-1, A-2, B-1.
        </p>
      </div>

      {status.kind === "loading" && kinds.length === 0 && <p className="opacity-60">Loading the tables…</p>}
      {status.kind === "error" && <p className="note note-danger">{status.message}</p>}
      {removeError && <p className="note note-danger">{removeError}</p>}
      {refundError && <p className="note note-danger">{refundError}</p>}

      {kinds.length > 0 && (
        <ul className="space-y-2">
          {kinds.map((kind) =>
            editingId === kind.id ? (
              <li key={kind.id}>
                <form onSubmit={save} className="flex flex-col gap-2.5 rounded-row border border-line p-3.5">
                  <KindFields
                    draft={editDraft}
                    errors={editErrors}
                    onChange={actions.setEditField}
                    idPrefix={`table-kind-${kind.id}`}
                  />
                  {editForm.error && <p className="note note-danger">{editForm.error}</p>}
                  <div className="flex justify-end gap-2">
                    <button type="button" className="btn-ghost" onClick={actions.cancelEdit}>
                      Cancel
                    </button>
                    <button type="submit" className="btn-primary btn-sm" disabled={editForm.busy}>
                      {editForm.busy ? "Saving…" : "Save"}
                    </button>
                  </div>
                </form>
              </li>
            ) : (
              <KindRow
                key={kind.id}
                kind={kind}
                removing={removingId === kind.id}
                cancelled={cancelled}
                confirmRefundId={confirmRefundId}
                refundingId={refundingId}
              />
            ),
          )}
        </ul>
      )}

      {status.kind === "loaded" && !cancelled && (
        <form onSubmit={add} className="flex flex-col gap-2.5 rounded-row border border-line p-3.5">
          <p className="text-[13px] font-semibold text-white">Add a kind of table</p>
          <KindFields draft={draft} errors={errors} onChange={actions.setField} idPrefix="table-kind-new" />
          {addForm.error && <p className="note note-danger">{addForm.error}</p>}
          <button type="submit" className="btn-primary btn-sm self-start" disabled={addForm.busy}>
            {addForm.busy ? "Adding…" : "+ Add tables"}
          </button>
        </form>
      )}
    </section>
  );
}
